/**
 * Thin fetch wrapper: JSON in/out, RFC 9457 problems → `ApiError`, network failures → `NetworkError`,
 * `Idempotency-Key` on mutations, credentials included (claim session cookie, §11.1).
 */
import type { ProblemDetails } from '@windrop/contracts';
import { apiUrl } from '../config';
import { IDEMPOTENCY_KEY_HEADER, PROBLEM_CONTENT_TYPE, PROBLEM_TYPE_BASE } from './constants';
import { ApiError, NetworkError, type RequestOptions } from './types';

export interface HttpRequest extends RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

const problemFallback = (status: number, title: string, detail?: string): ProblemDetails => ({
  type: `${PROBLEM_TYPE_BASE}${status >= 500 ? 'provider_unavailable' : 'not_found'}`,
  title,
  status,
  detail,
});

export function withQuery(path: string, query?: HttpRequest['query']): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined) qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

export async function http<T>(path: string, req: HttpRequest = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (req.body !== undefined) headers['Content-Type'] = 'application/json';
  if (req.idempotencyKey) headers[IDEMPOTENCY_KEY_HEADER] = req.idempotencyKey;
  let res: Response;
  try {
    res = await fetch(apiUrl(withQuery(path, req.query)), {
      method: req.method ?? (req.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
      credentials: 'include',
      signal: req.signal,
    });
  } catch (cause) {
    throw new NetworkError('network_unavailable', cause);
  }
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok) {
    if (contentType.includes(PROBLEM_CONTENT_TYPE) || contentType.includes('application/json')) {
      const body = (await res.json().catch(() => null)) as Partial<ProblemDetails> | null;
      if (body && typeof body.type === 'string' && typeof body.status === 'number')
        throw new ApiError(body as ProblemDetails);
      throw new ApiError(problemFallback(res.status, res.statusText || 'Request failed', body?.detail));
    }
    throw new ApiError(problemFallback(res.status, res.statusText || 'Request failed'));
  }
  return (await res.json()) as T;
}

/** Fire-and-forget POST that survives page hide (`sendBeacon`) and never throws. */
export async function beacon(path: string, body: unknown, useBeacon = false): Promise<void> {
  const url = apiUrl(path);
  const json = JSON.stringify(body);
  try {
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(url, new Blob([json], { type: 'application/json' }));
      if (ok) return;
    }
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      credentials: 'include',
      keepalive: true,
    });
  } catch {
    /* telemetry is best-effort */
  }
}
